import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import { useSiteContent } from "./siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DISHES = [
  {
    name: "Poulet grillé",
    desc: "Poulet grillé au feu de bois, piri-piri maison et frites.",
    price: 10.5,
    img: "https://images.unsplash.com/photo-1615557960916-5f4791effe9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxyb2FzdGVkJTIwY2hpY2tlbiUyMHJ1c3RpY3xlbnwwfHx8fDE3OTAzMjg2MDh8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Poulpe à Lagareiro",
    desc: "Rôti au four, huile d'olive fumée et pommes de terre grenaille.",
    price: 18.0,
    img: "https://images.pexels.com/photos/26586541/pexels-photo-26586541.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    name: "Riz aux Fruits de Mer",
    desc: "Bouillon riche de fruits de mer frais, coriandre et citron vert.",
    price: 16.5,
    img: "https://images.unsplash.com/photo-1676471932681-45fa972d848a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxnb3VybWV0JTIwZmluZSUyMGRpbmluZyUyMHNlYWZvb2QlMjBkaXNofGVufDB8fHx8MTc4NzMzNTkyMHww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Cataplana du Jour",
    desc: "Le meilleur de la pêche, servi dans la cataplana en cuivre.",
    price: 19.0,
    img: "https://images.unsplash.com/photo-1676471926534-d5c9771909fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwZmluZSUyMGRpbmluZyUyMHNlYWZvb2QlMjBkaXNofGVufDB8fHx8MTc4NzMzNTkyMHww&ixlib=rb-4.1.0&q=85",
  },
  {
    name: "Cochon de Lait Croustillant",
    desc: "À la façon de Bairrada, orange et pommes de terre rustiques.",
    price: 15.0,
    img: "https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=940&auto=format&fit=crop",
  },
  {
    name: "Pastéis & Café",
    desc: "Pastel de nata tiède, cannelle et un bon expresso.",
    price: 6.5,
    img: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=940&auto=format&fit=crop",
  },
];

export function useDishes() {
  const [dishes, setDishes] = useState(DISHES);
  useEffect(() => {
    axios
      .get(`${API}/dishes`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setDishes(r.data);
      })
      .catch(() => {});
  }, []);
  return dishes;
}

export function useGallery() {
  const [items, setItems] = useState(
    DISHES.map((d) => ({ name: d.name, img: d.img }))
  );
  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setItems(r.data);
      })
      .catch(() => {});
  }, []);
  return items;
}

export default function Dishes() {
  const photos = useGallery();
  const site = useSiteContent();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
  });
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!emblaApi || paused) return;
    const id = setInterval(() => emblaApi.scrollNext(), 3800);
    return () => clearInterval(id);
  }, [emblaApi, paused]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section
      id="pratos"
      data-testid="dishes-section"
      className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <h2 className="font-display text-4xl italic tracking-tight sm:text-5xl lg:text-6xl">
            {site.dishes_title}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <Link
            data-testid="dishes-voir-plus-btn"
            to="/galerie"
            className="rounded-full bg-[#102C1F] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            {site.dishes_more}
          </Link>
          <button
            data-testid="carousel-prev-btn"
            onClick={prev}
            aria-label="Plat précédent"
            className="rounded-full border border-[#102C1F]/25 p-3 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            data-testid="carousel-next-btn"
            onClick={next}
            aria-label="Plat suivant"
            className="rounded-full border border-[#102C1F]/25 p-3 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </motion.div>

      <div
        ref={emblaRef}
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex gap-6 md:gap-10">
          {photos.map((dish, i) => (
            <motion.article
              key={dish.name}
              data-testid={`dish-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.8,
                delay: (i % 3) * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="group w-[50%] shrink-0 sm:w-[30%] lg:w-[20%]"
            >
              <div className="aspect-[4/5] w-full overflow-hidden rounded-sm bg-[#102C1F]/5">
                <img
                  src={dish.img}
                  alt={dish.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
